import type { ReactNode, RefObject, UIEvent } from 'react';
import { useRef } from 'react';
import styled, { css } from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { PROJECTION_WITH_DIFFERENCE_BY_SPORT } from '#src/lib/projection.ts';
import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';

const Root = styled(Vertical).attrs({ gap: 0 })`
  isolation: isolate;

  --stat-columns: 2;
  --fixed-columns: 2;
  --matchups-columns: 0;
  --grow-columns: 0;
  --all-columns: calc(
    var(--stat-columns) + var(--fixed-columns) + var(--grow-columns) +
      var(--matchups-columns)
  );

  --player-metas-width: 172px;
  --player-matchups-width: 140px;
  --player-stats-icon-width: 0px;
  --player-grade-width: 0px;
  --player-gaps: calc(2 * var(--unit));

  --player-cell-width: calc(
    var(--player-metas-width) + var(--player-stats-icon-width) +
      var(--player-gaps)
  );
  --player-cell-price: 120px;

  --padding-first-cell-left: var(--unit);
  --padding-first-cell-right: var(--unit);
  --padding-last-cell-left: var(--unit);
  --padding-last-cell-right: var(--double-unit);

  @media ${laptopAndAbove} {
    --fixed-columns: 2;
    --grow-columns: 1;
    --matchups-columns: 1;
    --player-metas-width: 272px;
    --player-stats-icon-width: 80px;
    --player-grade-width: 40px;
    --player-gaps: calc(6 * var(--unit));

    --player-cell-price: 176px;

    --padding-first-cell-left: var(--triple-unit);
    --padding-first-cell-right: var(--intermediate-unit);
    --padding-last-cell-left: var(--intermediate-unit);
    --padding-last-cell-right: var(--triple-unit);
  }
`;

const tablePart = css`
  display: grid;
  width: 100%;
  overflow: scroll;
  gap: 0;
  grid-template-columns:
    var(--player-cell-width) repeat(var(--stat-columns), 48px)
    minmax(var(--player-cell-price), 1fr);
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  overscroll-behavior-x: none;
  @media ${tabletAndAbove} {
    border-left: solid 1px var(--c-nd-150-opaque);
    border-right: solid 1px var(--c-nd-150-opaque);
  }
  @media ${laptopAndAbove} {
    --player-matchups-width: 176px;
    grid-template-columns:
      var(--player-cell-width) var(--player-matchups-width)
      repeat(var(--stat-columns), 62px) minmax(0px, 1fr) var(
        --player-cell-price
      );
  }
`;

const TableBody = styled.div`
  ${tablePart}
  border-bottom: solid 1px var(--c-nd-150-opaque);
`;

const TableHeaderWrapper = styled.div`
  position: sticky;
  top: var(--current-stack-height);
  z-index: 10;
  background: black;
`;
const TableHeader = styled.div`
  ${tablePart}
  border-top: solid 1px var(--c-nd-150-opaque);
  border-bottom: 1px solid var(--c-nd-100-opaque);

  @media ${tabletAndAbove} {
    border-radius: var(--unit) var(--unit) 0 0;
  }
`;

type Props = {
  header: ReactNode;
  children: ReactNode;
  nbStatsColumns?: number;
  className?: string;
};

export const SearchTable = ({
  header,
  children,
  nbStatsColumns,
  className,
}: Props) => {
  const {
    flags: { useHandleL10Gain = false },
  } = useFeatureFlags();
  const { sport } = useSportContext();
  const withDifference =
    useHandleL10Gain && PROJECTION_WITH_DIFFERENCE_BY_SPORT[sport];
  const { statsManager, displayableStats } = useManagePlayerSearchView();

  const nbProjectionColumns =
    statsManager[PlayerSearchViewAttributes.PROJECTION].length *
    (withDifference ? 2 : 1);

  const scrollHeaderRef = useRef<HTMLDivElement>(null);
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  const onScroll = (
    event: UIEvent<HTMLDivElement>,
    target: RefObject<HTMLDivElement | null>
  ) => {
    if (!target.current) return;
    target.current.scrollLeft = event.currentTarget.scrollLeft;
  };
  return (
    <Root
      style={
        {
          '--stat-columns':
            nbStatsColumns ||
            statsManager[PlayerSearchViewAttributes.AVERAGE_PLAYER_SCORE]
              .length +
              displayableStats.length +
              statsManager[PlayerSearchViewAttributes.BREAKDOWN].length +
              nbProjectionColumns +
              statsManager[PlayerSearchViewAttributes.PROJECTION_GRADE].length,
        } as React.CSSProperties
      }
    >
      <TableHeaderWrapper>
        <TableHeader
          ref={scrollHeaderRef}
          className={className}
          onScroll={e => onScroll(e, scrollBodyRef)}
        >
          {header}
        </TableHeader>
      </TableHeaderWrapper>
      <TableBody
        ref={scrollBodyRef}
        className={className}
        onScroll={e => onScroll(e, scrollHeaderRef)}
      >
        {children}
      </TableBody>
    </Root>
  );
};
